import { useState } from "react";
import { Link } from "react-router";
import { useCanWrite } from "../auth/AuthProvider";
import { DeleteWorkloadsModal } from "../components/DeleteWorkloadsModal";
import { Pagination } from "../components/Pagination";
import { SelectionBar } from "../components/SelectionBar";
import { SearchInput } from "../components/SearchInput";
import {
  Alert,
  Button,
  Card,
  EmptyState,
  TableSkeleton,
  PageHeader,
  SuspendedBadge,
  Table,
  Td,
  Th,
} from "../components/ui";
import { buttonStyles, cx } from "../components/ui";
import { formatDate } from "../lib/format";
import { groupLabel, groupLabels } from "../lib/regions";
import { useOrgs, usePendingQuotaRequests, usePurgeOrganizations } from "../lib/queries";
import type { AdminOrg, WorkloadResult } from "../lib/types";

const LIMIT = 25;

export function OrgsPage() {
  const [q, setQ] = useState("");
  const [offset, setOffset] = useState(0);
  const [suspendedOnly, setSuspendedOnly] = useState(false);
  const { data, isPending, error } = useOrgs({ q, limit: LIMIT, offset, suspended: suspendedOnly });
  const canWrite = useCanWrite();
  const pendingQuota = usePendingQuotaRequests();

  /*
   * Selection, for the one bulk action this page has: clearing the workloads
   * of several suspended tenants at once. The rows are selectable whatever
   * the filter says — a suspension is the usual reason and not a rule — but
   * the toggle is what makes a page of them.
   */
  const [selected, setSelected] = useState<Map<string, AdminOrg>>(() => new Map());
  const [purging, setPurging] = useState(false);
  const [results, setResults] = useState<WorkloadResult[] | null>(null);
  const purge = usePurgeOrganizations();

  function search(next: string) {
    setQ(next);
    setOffset(0);
  }

  function toggle(org: AdminOrg) {
    setSelected((current) => {
      const next = new Map(current);
      if (next.has(org.id)) next.delete(org.id);
      else next.set(org.id, org);
      return next;
    });
  }

  const chosen = [...selected.values()];
  const chosenApps = chosen.reduce((sum, org) => sum + (org.counts?.apps ?? 0), 0);
  const notSuspended = chosen.filter((org) => !org.suspendedAt).length;
  const pageIds = data?.data.map((org) => org.id) ?? [];
  const allOnPage = pageIds.length > 0 && pageIds.every((id) => selected.has(id));

  return (
    <>
      <PageHeader
        title="Organizations"
        description="Each maps to one project on its assigned server."
        action={
          <Link to="/organizations/limit-requests" className={buttonStyles("secondary", "sm")}>
            Limit requests{pendingQuota.data ? ` (${pendingQuota.data})` : ""}
          </Link>
        }
      />

      <Card>
        <div className="flex flex-wrap items-center gap-3 border-b border-line px-5 py-3">
          <div className="min-w-0 flex-1">
            <SearchInput value={q} onChange={search} placeholder="Search by name or slug…" />
          </div>
          {/* A segmented pair rather than a checkbox: the two are states of the
              page, not a setting, and the selected one should read as where
              you are. */}
          <div role="group" aria-label="Show" className="flex rounded-md border border-line p-0.5">
            {([false, true] as const).map((only) => (
              <button
                key={String(only)}
                type="button"
                aria-pressed={suspendedOnly === only}
                onClick={() => {
                  setSuspendedOnly(only);
                  setOffset(0);
                }}
                className={cx(
                  "rounded px-3 py-1 text-xs transition-colors duration-150",
                  suspendedOnly === only ? "bg-raised text-fg" : "text-fg-subtle hover:text-fg",
                )}
              >
                {only ? "Suspended" : "All"}
              </button>
            ))}
          </div>
        </div>

        {isPending ? (
          <TableSkeleton columns={6} />
        ) : error ? (
          <div className="p-5">
            <Alert>{error.message}</Alert>
          </div>
        ) : data.data.length === 0 ? (
          <EmptyState
            title={
              q
                ? "No matching organizations"
                : suspendedOnly
                  ? "No suspended organizations"
                  : "No organizations yet"
            }
            description={q ? "Try a different search." : undefined}
          />
        ) : (
          <>
            <Table
              head={
                <tr>
                  {canWrite && (
                    <Th className="w-8">
                      <input
                        type="checkbox"
                        aria-label="Select every organization on this page"
                        checked={allOnPage}
                        onChange={() =>
                          setSelected((current) => {
                            const next = new Map(current);
                            if (allOnPage) for (const id of pageIds) next.delete(id);
                            else for (const org of data.data) next.set(org.id, org);
                            return next;
                          })
                        }
                        className="h-4 w-4 rounded border-line text-accent focus:ring-2 focus:ring-accent/20"
                      />
                    </Th>
                  )}
                  <Th>Organization</Th>
                  <Th priority="md">Regions</Th>
                  <Th numeric priority="lg">Members</Th>
                  <Th numeric priority="sm">Apps</Th>
                  <Th>Status</Th>
                  <Th priority="lg">Created</Th>
                </tr>
              }
            >
              {data.data.map((org) => (
                <tr key={org.id} className="hover:bg-raised">
                  {canWrite && (
                    <Td>
                      <input
                        type="checkbox"
                        aria-label={`Select ${org.name}`}
                        checked={selected.has(org.id)}
                        onChange={() => toggle(org)}
                        className="h-4 w-4 rounded border-line text-accent focus:ring-2 focus:ring-accent/20"
                      />
                    </Td>
                  )}
                  <Td>
                    <Link
                      to={`/organizations/${org.id}`}
                      className="font-medium text-accent hover:underline"
                    >
                      {org.name}
                    </Link>
                    <p className="text-xs text-fg-subtle">{org.slug}</p>
                  </Td>
                  <Td priority="md">
                    {org.regions.length > 0 ? (
                      groupLabels(org.regions).join(", ")
                    ) : (
                      <span className="text-fg-subtle">Unplaced</span>
                    )}
                  </Td>
                  <Td numeric priority="lg">{org.counts?.members ?? 0}</Td>
                  <Td numeric priority="sm">{org.counts?.apps ?? 0}</Td>
                  <Td>
                    {org.suspendedAt ? <SuspendedBadge /> : org.closesAt ? "Closing" : "Active"}
                  </Td>
                  <Td className="text-fg-subtle" priority="lg">{formatDate(org.createdAt)}</Td>
                </tr>
              ))}
            </Table>
            <Pagination
              total={data.total}
              limit={data.limit}
              offset={data.offset}
              onChange={setOffset}
            />
          </>
        )}
      </Card>

      {canWrite && (
        <SelectionBar
          count={chosen.length}
          noun="organization"
          onClear={() => setSelected(new Map())}
          action={
            <Button variant="danger" size="sm" icon="trash" onClick={() => setPurging(true)}>
              Delete all workloads
            </Button>
          }
        >
          {chosenApps} app{chosenApps === 1 ? "" : "s"} at least
          {notSuspended > 0 && ` · ${notSuspended} not suspended`}
        </SelectionBar>
      )}

      {/*
        The name typed is the word DELETE rather than an organization's, because
        there are several. The manifest lists the organizations; the report
        lists every workload the API found in them, prefixed with whose it was.
      */}
      <DeleteWorkloadsModal
        open={purging}
        onClose={() => setPurging(false)}
        title={`Delete all workloads of ${chosen.length} organization${chosen.length === 1 ? "" : "s"}`}
        description="Every app and database each of them has. The organizations themselves stay."
        items={chosen.map((org) => ({
          kind: "organization" as const,
          id: org.id,
          name: org.name,
          detail: `${org.counts?.apps ?? 0} app${(org.counts?.apps ?? 0) === 1 ? "" : "s"}${org.regions.length > 0 ? ` · ${org.regions.map((code) => groupLabel(code) ?? code).join(", ")}` : ""}`,
          flag: org.suspendedAt ? undefined : "not suspended",
        }))}
        noun="organization"
        confirmName="DELETE"
        confirmLabel="Delete all workloads"
        run={async ({ force, otp }) => {
          const response = await purge.mutateAsync({ orgIds: chosen.map((org) => org.id), force, otp });
          setResults(
            response.organizations.flatMap((org) =>
              org.found
                ? org.results.map((row) => ({ ...row, name: `${org.name} · ${row.name}` }))
                : [{ kind: "app" as const, id: org.id, name: org.id, deleted: false, warnings: [], error: "Organization not found" }],
            ),
          );
        }}
        pending={purge.isPending}
        results={results}
        onDone={() => {
          setPurging(false);
          setResults(null);
          setSelected(new Map());
        }}
      />
    </>
  );
}
